import React, { useEffect, useMemo } from "react";
import styled from "styled-components";
import { includes, omit } from "lodash";
import { RouteComponentProps, withRouter } from "react-router";
import { CalendarDay } from "@sonder/ui/source/styled/components/CalendarDay";
import { MultiAvatar } from "@sonder/ui/source/styled/components/MultiAvatar";
import { Checkbox } from "@sonder/ui/source/styled/fragments/Checkbox";
import { Invisible } from "@sonder/ui/source/styled/fragments/Invisible";
import {
  Cell,
  useTableContext,
} from "@sonder/ui/source/styled/fragments/Table";
import { Tag } from "@sonder/ui/source/styled/fragments/Tag";
import { colors } from "@sonder/ui/source/styled/theme";
import { useSegment } from "analytics/segment";
import { useLayout } from "core/components/LayoutProvider";
import {
  OccupancyState,
  OccupancyStatus,
  mapCheckoutStateToTag,
} from "core/queries/mappers";
import { toggleTask } from "tasks/actions";
import { AssigneePicker } from "tasks/components/AssigneePicker";
import { CompleteTaskButton } from "tasks/components/CompleteTaskButton";
import { TaskDescription } from "tasks/components/TaskDescription";
import {
  TaskDueByDate,
  TaskDueByDateInlineTrigger,
} from "tasks/components/TaskDueByDate";
import { TaskPriority } from "tasks/components/TaskPriority";
import { convertToMoment, isCleanTask, isSpaceCleanTask } from "tasks/helpers";
import { useStore } from "tasks/store";
import { FilterStatus } from "typings/globalGQLTypes";

// Helpers

const mapStatusToLabel = (status: string | null) => {
  switch (status) {
    case FilterStatus.canceled:
      return "Archived";
    case FilterStatus.complete:
      return "Completed";
    case FilterStatus.inprogress:
      return "In progress";
    case FilterStatus.enroute:
      return "En route";
    case FilterStatus.ready:
    default:
      return "Ready";
  }
};

const EllipsisText = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
`;

const TagsList = styled.div`
  align-items: center;
  display: flex;

  > span {
    & + span {
      margin-left: 0.3rem;
    }
  }
`;

const UnreadIndicator = styled.span`
  color: ${colors.greenDark};
`;

interface TaskListRowProps extends Task, RouteComponentProps {
  firstRow?: boolean;
  lastRow?: boolean;
}
export const Row = withRouter((task: TaskListRowProps) => {
  const {
    assigneeIds,
    bookingInfo,
    cityId,
    description,
    dueBy,
    history,
    futureCheckouts,
    id: taskId,
    internalRef,
    isCheckedInToday,
    isCheckedOutToday,
    isOccupiedAllDay,
    firstRow,
    lastRow,
    location,
    match,
    occupancyStatus,
    priority,
    recurrenceSettings,
    status,
    subjectType,
    type,
    unreadCount,
  } = task;
  const {
    dispatch,
    state: { selectedTasks },
  } = useStore();
  const { track } = useSegment();
  const { sidepanel } = useLayout();
  const { hovered, selected, setSelected } = useTableContext();

  useEffect(() => {
    const numericTaskId = taskId.split("|").pop() || "";
    if (selected !== taskId && location.pathname.includes(numericTaskId)) {
      setSelected(taskId);
    }
    if (selected === taskId && !location.pathname.includes(numericTaskId)) {
      setSelected(null);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (selected === taskId) {
      const numericTaskId = selected.split("|").pop();
      history.push(`${match.url}/${numericTaskId}`);
    }
  }, [selected]);

  const checkoutState = mapCheckoutStateToTag(
    isCheckedInToday,
    isCheckedOutToday,
    isOccupiedAllDay,
  );
  const statusLabel = mapStatusToLabel(status);
  const isUnread = !!unreadCount;
  const isSelected = useMemo(
    () => includes(selectedTasks, taskId),
    [selectedTasks],
  );

  return (
    <>
      <Cell rowStart firstRow={firstRow} lastRow={lastRow} rowKey={taskId}>
        <Checkbox
          checked={isSelected}
          onClick={e => {
            track({
              eventName: "row",
              action: "click",
              element: "toggleTask",
              props: {
                taskId,
                selected: !isSelected,
              },
            });
            dispatch(toggleTask(taskId));
            e.stopPropagation();
          }}
        />
      </Cell>
      <Cell rowKey={taskId} title={description}>
        {isUnread && <UnreadIndicator>●</UnreadIndicator>}
      </Cell>
      <Cell rowKey={taskId} title={description}>
        <TaskDescription
          description={description}
          eventName="task_row"
          taskId={taskId}
        />
      </Cell>
      <Cell rowKey={taskId}>
        <Invisible hidden={!priority && hovered !== taskId}>
          <TaskPriority
            eventName="task_row"
            priority={priority}
            taskId={taskId}
          />
        </Invisible>
      </Cell>
      <Cell rowKey={taskId} title={internalRef || ""}>
        <EllipsisText>{internalRef}</EllipsisText>
      </Cell>
      <Cell rowKey={taskId}>
        <Invisible hidden={!assigneeIds.length && hovered !== taskId}>
          <AssigneePicker
            assigneeIds={assigneeIds}
            disabled={isSpaceCleanTask(type)}
            eventName="task_row"
            renderTrigger={({ assignees }) => (
              <MultiAvatar
                avatars={assignees}
                maxLength={3}
                onClick={e => e.stopPropagation()}
              />
            )}
            taskCityId={cityId}
            taskId={taskId}
            taskType={type}
          />
        </Invisible>
      </Cell>
      <Cell rowKey={taskId}>
        <Invisible hidden={!dueBy && hovered !== taskId}>
          <TaskDueByDate
            alignContent="right"
            bookingInfo={bookingInfo}
            date={convertToMoment(dueBy)}
            disabled={isCleanTask(type)}
            eventName="task_row"
            futureCheckouts={futureCheckouts}
            recurrenceSetting={recurrenceSettings}
            renderTrigger={props => (
              <TaskDueByDateInlineTrigger
                {...props}
                onClick={e => e.stopPropagation()}
              />
            )}
            subjectType={subjectType}
            taskId={taskId}
          />
        </Invisible>
      </Cell>
      <Cell rowKey={taskId} title={checkoutState}>
        <CalendarDay
          isCheckedInToday={isCheckedInToday}
          isCheckedOutToday={isCheckedOutToday}
          isOccupiedAllDay={isOccupiedAllDay}
        />
      </Cell>
      {sidepanel === "collapsed" && (
        <>
          <Cell rowKey={taskId} title={statusLabel}>
            {statusLabel}
          </Cell>
          <Cell rowKey={taskId}>
            <TagsList>
              {occupancyStatus ===
                OccupancyStatus[OccupancyState.DuringStay] && (
                <Tag filled color="purple">
                  {occupancyStatus}
                </Tag>
              )}
            </TagsList>
          </Cell>
        </>
      )}
      <Cell rowEnd firstRow={firstRow} lastRow={lastRow} rowKey={taskId}>
        <CompleteTaskButton
          backgroundVariant="light"
          description={description}
          eventName="task_row"
          task={omit(task, ["history", "location", "match", "staticContext"])}
        />
      </Cell>
    </>
  );
});
